"use client";

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Eye, RefreshCw, AlertCircle } from "lucide-react";
import { formatCurrency, formatDate } from "@/lib/utils";
import { AppointmentStatus, Appointment } from "@/types";
import { toast } from "sonner";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import CommonHeader from "./common/CommonHeader";
import { AppointmentCalendarView } from "./appointment/AppointmentCalendarView";
import {
  AppointmentFilter,
  AppointmentFilters,
} from "./appointment/AppointmentFilter";
import { AppointmentsTable } from "./appointment/AppointmentsTable";
import { AppointmentDetailDialog } from "./appointment/AppointmentDetailDialog";
import { useDebounce } from "@/hooks/useDebounce";
import { AppointmentQueryParams } from "@/types/appointment.api.types";

// Convert filter state to API query parameters
const convertFiltersToParams = (
  filters: AppointmentFilters
): AppointmentQueryParams => {
  const params: AppointmentQueryParams = {};

  if (filters.status !== "all") {
    params.status = filters.status;
  }

  if (filters.customerId !== "all") {
    params.customerId = filters.customerId;
  }

  if (filters.staffId !== "all") {
    params.staffId = filters.staffId;
  }

  if (filters.startDate) {
    // Format as YYYY-MM-DD
    params.startDate = filters.startDate.toISOString().split("T")[0];
  }

  if (filters.endDate) {
    // Format as YYYY-MM-DD
    params.endDate = filters.endDate.toISOString().split("T")[0];
  }

  if (filters.search.trim()) {
    params.search = filters.search.trim();
  }

  return params;
};

export function AppointmentManagement() {
  const navigate = useNavigate();
  const {
    appointments,
    appointmentsLoading,
    appointmentsError,
    customers,
    vehicles,
    staff,
    updateAppointment,
    refreshAppointments,
    fetchAppointmentsWithFilters,
  } = useApp();

  const [activeTab, setActiveTab] = useState("list");
  const [selectedAppointment, setSelectedAppointment] =
    useState<Appointment | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Default filter: today to 3 days ahead
  const getDefaultDateFilter = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const threeDaysAhead = new Date(today);
    threeDaysAhead.setDate(today.getDate() + 3);
    threeDaysAhead.setHours(23, 59, 59, 999);
    return { startDate: today, endDate: threeDaysAhead };
  };

  const [showAllAppointments, setShowAllAppointments] = useState(false);
  const defaultDates = getDefaultDateFilter();

  const [filters, setFilters] = useState<AppointmentFilters>({
    status: "all",
    search: "",
    customerId: "all",
    staffId: "all",
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
  });

  // Debounce search input to avoid excessive API calls
  const debouncedSearch = useDebounce(filters.search, 500);

  // Trigger API call when filters change
  useEffect(() => {
    const params = convertFiltersToParams({
      ...filters,
      search: debouncedSearch,
    });

    fetchAppointmentsWithFilters(params);
  }, [
    filters.status,
    filters.customerId,
    filters.staffId,
    filters.startDate,
    filters.endDate,
    debouncedSearch,
    fetchAppointmentsWithFilters,
  ]);

  // Calendar days - generate full weeks for proper grid alignment
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    // Get the start of the week containing the first day of the month (Sunday = 0)
    const start = startOfWeek(monthStart, { weekStartsOn: 0 });
    // Get the end of the week containing the last day of the month
    const end = endOfWeek(monthEnd, { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const handleStatusUpdate = async (
    appointmentId: string,
    newStatus: AppointmentStatus
  ) => {
    try {
      // Pass the status directly - the appointmentService will handle mapping
      await updateAppointment(appointmentId, { status: newStatus as any });
      toast.success(
        `Appointment status updated to ${newStatus.replace(/_/g, " ")}`
      );
    } catch (error) {
      // Error is already handled in AppContext with toast
    }
  };

  const getStatusBadge = (status: string) => {
    // Map API status values to colors with modern, accessible color scheme
    const colors: Record<string, string> = {
      // API status values (uppercase)
      PENDING:
        "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
      CONFIRMED: "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
      COMPLETED:
        "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200",
      CANCELLED: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
      IN_PROGRESS:
        "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200",
      NOT_COMPLETED:
        "bg-pink-100 text-pink-800 border-pink-200 hover:bg-pink-200",
      POSTPONED:
        "bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-200",
      // Legacy lowercase/kebab-case values (for backward compatibility)
      "confirmation-pending":
        "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
      confirmed: "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
      "on-the-way":
        "bg-cyan-100 text-cyan-800 border-cyan-200 hover:bg-cyan-200",
      "in-progress":
        "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200",
      completed:
        "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200",
      cancelled: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
      "no-answer":
        "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200",
      "no-booking":
        "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200",
      "not-available":
        "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200",
      "not-completed":
        "bg-pink-100 text-pink-800 border-pink-200 hover:bg-pink-200",
      postponed:
        "bg-indigo-100 text-indigo-800 border-indigo-200 hover:bg-indigo-200",
    };

    // Format status text for display (null-safe)
    const formatStatusText = (statusText?: string): string => {
      const st = String(statusText || "");
      if (!st) return "Unknown";
      // Handle uppercase API values
      if (st === st.toUpperCase()) {
        return st.charAt(0) + st.slice(1).toLowerCase();
      }
      // Handle kebab-case values
      return st
        .split("-")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    };

    return (
      <Badge
        variant="outline"
        className={
          colors[status] || "bg-gray-100 text-gray-800 border-gray-200"
        }
      >
        {formatStatusText(status)}
      </Badge>
    );
  };

  // Loading skeleton
  if (appointmentsLoading && appointments.length === 0) {
    return (
      <div className="space-y-6 w-full overflow-x-hidden">
        <CommonHeader
          title="Appointment Management"
          description="Schedule and manage appointments"
          onClick={() => navigate("/appointments/new")}
          buttonText="New Appointment"
          buttonIcon={Plus}
        />
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Appointment Management
          </h1>
          <p className="text-muted-foreground">
            Schedule and manage appointments
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showAllAppointments ? "default" : "outline"}
            onClick={() => {
              setShowAllAppointments(!showAllAppointments);
              if (!showAllAppointments) {
                // Show all - clear date filter
                setFilters({ ...filters, startDate: undefined, endDate: undefined });
              } else {
                // Show 3 days - set default date filter
                const dates = getDefaultDateFilter();
                setFilters({ ...filters, startDate: dates.startDate, endDate: dates.endDate });
              }
            }}
          >
            {showAllAppointments ? "Showing All" : "Next 3 Days"}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={refreshAppointments}
            disabled={appointmentsLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${appointmentsLoading ? "animate-spin" : ""}`}
            />
          </Button>
          <Button onClick={() => navigate("/appointments/new")}>
            <Plus className="h-4 w-4 mr-2" />
            New Appointment
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {appointmentsError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>{appointmentsError}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshAppointments}
              disabled={appointmentsLoading}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="list">List View</TabsTrigger>
        </TabsList>

        {/* Calendar View */}
        <TabsContent value="calendar" className="space-y-4">
          {appointmentsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          ) : (
            <AppointmentCalendarView
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              calendarDays={calendarDays}
              appointments={appointments}
              customers={customers}
              onAppointmentClick={setSelectedAppointment}
            />
          )}
        </TabsContent>

        {/* List View */}
        <TabsContent value="list" className="space-y-4">
          {/* Filters */}
          <AppointmentFilter
            filterStatus={filters.status}
            onFilterChange={setFilters}
            customers={customers}
            staff={staff}
          />

          {/* Appointments Table */}
          {appointmentsLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <AppointmentsTable
              appointments={appointments}
              customers={customers}
              vehicles={vehicles}
              staff={staff}
              getStatusBadge={getStatusBadge}
              onStatusUpdate={handleStatusUpdate}
              onView={setSelectedAppointment}
              isLoading={appointmentsLoading}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Appointment Detail Dialog */}
      <AppointmentDetailDialog
        open={!!selectedAppointment}
        onOpenChange={(open) => !open && setSelectedAppointment(null)}
        appointment={selectedAppointment}
        customers={customers}
        vehicles={vehicles}
        staff={staff}
        getStatusBadge={getStatusBadge}
      />
    </div>
  );
}
